import { useEffect, useRef, useState } from 'react';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { PieChart } from '@mui/x-charts';
import { useNavigate } from 'react-router-dom';

const StyledText = styled('text')(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: 20,
}));

function PieCenterLabel({ children }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <StyledText x={left + width / 2} y={top + height / 2}>
      {children}
    </StyledText>
  );
}
PieCenterLabel.propTypes = {
  children: PropTypes.node.isRequired,
};

const CertificationPieChart = ({ graphData, total, selectedYear }) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 300, height: 300 });

  // Dynamically resize based on parent container
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { offsetWidth } = containerRef.current;
        const newSize = Math.min(offsetWidth, 350); // limit max size
        setSize({ width: newSize, height: newSize });
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      ref={containerRef}
      className="donut-chart"
      style={{
        width: '100%',
        maxWidth: '400px',
        height: 'auto',
        margin: '0 auto',
      }}
    >
      <PieChart
        margin={{ right: 0, bottom: 10 }}
        slotProps={{
          legend: { hidden: true },
        }}
        colors={['#6b5ce8', '#a29aff', '#c5c0ff', '#e8e6ff']}
        series={[
          {
            innerRadius: size.width / 4,
            outerRadius: size.width / 2.5,
            data: graphData,
          },
        ]}
        width={size.width}
        height={size.height}
        onItemClick={(event, data) => {
          const clickedData = graphData[data.dataIndex];
          const queryParams = new URLSearchParams({
            certification: clickedData.label,
            year: selectedYear,
          }).toString();
          navigate(`/certifications/map?${queryParams}`, {
            state: { fromPage: true, userSelectedYear: selectedYear },
          });
        }}
      >
        {total && <PieCenterLabel>{`Total:\n${total}`}</PieCenterLabel>}
      </PieChart>
    </div>
  );
};

CertificationPieChart.propTypes = {
  graphData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ).isRequired,
  total: PropTypes.number.isRequired,
  selectedYear: PropTypes.number.isRequired,
};

export default CertificationPieChart;
